import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import './sampleTransferDetail.css';
import '../backlog/backlog.css';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useNotify } from '../../components/notifications';
import { useConfirm, useReasonGate, showDialog } from '../../components/confirm';
import { runLoading } from '../../components/loading';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import { NotesListPopup, useNotesCount } from '../analysis/SdmsProjectNotes';
import { useNavActions } from '../../hooks/useNavActions';
import { useModalKeys } from '../../hooks/useModalKeys';
import { useAuthStore } from '../../auth/store';
import SampleTransferBottomTabs from './SampleTransferBottomTabs';
import { StxModalHead } from './StxModalHead';
import { LookupCombo } from '../../components/LookupCombo';
import { useUnsavedGuard, resetUnsavedTracking } from '../../hooks/useUnsavedGuard';
import SampleTransferSamplesPage, { type PillTab } from './SampleTransferSamplesPage';

/** A naive local-time stamp (YYYY-MM-DDTHH:mm:ss) for the moment a sample is ticked —
 *  the legacy `ScanDateTime = DateTime.Now`. Rendered 24h via fmtLocalDateTime. */
function nowStamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// ─── Types (match the API DTOs) ──────────────────────────────────────────────
interface Bottle {
  bottleOid: string; jobId: string; sampleId: string; sampleNo: number; sampleName: string;
  testName: string; bottleId: string; phCheck: string; sampleStatus: string; sampleStatusOid: string | null;
  remark: string; relinquishedBy: string; recordedBy: string; recordedDateTime: string | null;
  receiptMode: string; stageInt: number; statusId: string; status: string;
  receivedDate: string | null; sampleMatrix: string;
}

/** One bottle's buffered inline edits. Only the cells the user actually touched
 *  are present, so Save never overwrites a column nobody edited.
 *  Mirrors the five PERSISTED editable columns of ALPACA's All Samples grid
 *  (Model.xafml SampleBottleAllocation_ListView_SampleTransfer, InlineEditMode
 *  "Batch"). Test and Remark look editable there but are [NonPersistent]
 *  getter-only on the BO (NPTest computes the joined test names; IsRemark is
 *  just Remark <> ''), so XAF cannot store an edit to either - they stay
 *  read-only here too. */
/** The 'no status' entry in the SampleStatus cell dropdown. Not a dbo.SampleStatus
 *  row - picking it clears the column. */
const STATUS_NONE = 'N/A';

interface BottleEdit {
  phCheck?: string;
  relinquishedByOid?: string | null;
  sampleStatusOid?: string | null;
  recordedByOid?: string | null;
  recordedDateTime?: string | null;
}
interface ReceivedRow {
  receivedDateOid: string; receivedDate: string | null; receivedBy: string;
  relinquishedBy: string; noOfSamples: number; coolerId: string;
}
interface Header {
  jobOid: string; jobId: string; client: string; projectId: string; projectName: string;
  projectLocation: string; sampleMatrix: string; testSummary: string;
  receivedDate: string | null; receivedBy: string; relinquishedByIds: string; relinquishedBy: string;
  coolerId: string; coolerTemp: string; noSamples: number; noContainers: number; noCoolers: number;
  sampleAppearance: string; transportCondition: string; comment: string; sampleStatusOid: string | null;
  jobStatusId: string; jobStatus: string; dueDate: string | null; fieldDataComplete: boolean;
  samplesCount: number; testsCount: number; containersCount: number;
}
interface Detail { header: Header; bottles: Bottle[]; receivedDates: ReceivedRow[]; }
interface Lookup { id: string; name: string; }

function pill(statusId: string): string {
  if (statusId === 'ST-1-5') return 'ok';
  if (statusId === 'ST-1-6' || statusId === 'ST-1-3') return 'info';
  if (statusId === 'ST-1-1' || statusId === 'ST-1-4') return 'warn';
  if (statusId === 'ST-1-2') return 'alert';
  return 'info';
}
/** Legacy Status field shows the TRANSFER status enum name ("Submitted"), NOT the
 *  ST-1-x StatusDefinition text ("Submitted, Pending Field Data Entry"). The stage
 *  is derivable from the ST-1-x id (LIMS DetailView binds SampleTransferStatus). */
function transferStatusName(statusId: string): string {
  if (statusId === 'ST-1-5' || statusId === 'ST-1-6') return 'Submitted';
  if (statusId === 'ST-1-3' || statusId === 'ST-1-4') return 'Pending Submission';
  if (statusId === 'ST-1-1' || statusId === 'ST-1-2') return 'Pending Transfer';
  return '';
}
function nowLocalInput(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
// A naive/ISO wire datetime → the <input type="datetime-local"> value (YYYY-MM-DDTHH:mm).
function toLocalInput(iso: string | null): string {
  if (!iso) return nowLocalInput();
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : nowLocalInput();
}

interface Props { rowKey: string; jobId: string; onBack: () => void; }

export default function SampleTransferDetailPage({ rowKey, jobId, onBack }: Props) {
  const notify = useNotify();
  const { confirm: confirmDlg } = useConfirm();
  const gateReason = useReasonGate();
  // Module Access Control (Role-Based MAC) — the SAME gate Sampling Allocation uses
  // (useNavActions). write → Save / Submit / Rollback + inline grid edits; delete →
  // the Delete action; create → the bottom-tab New actions. Falls open for admins /
  // before perms load / when the lab runs no MAC (see useNavActions). Read-level
  // actions (Reports / Export / Refresh / Project Notes view) stay visible.
  const perm = useNavActions('samptransfer');
  const canWrite = perm.write;
  const canDelete = perm.delete;
  const canCreate = perm.create;
  // Current user = the "Recorded By" stamped on a sample when it is ticked (legacy cpusername).
  const currentUser = useAuthStore(s => s.user?.name ?? '');
  // Per-bottle tick timestamp (the moment a checkbox was selected) — the RecordedDateTime.
  const [stamps, setStamps] = useState<Record<string, string>>({});
  // Bottles the user actively (re)selected THIS session. Only these get the live
  // who/when/how stamp (RecordedBy = me, RecordedDateTime = now, ReceiptMode =
  // Scanned/Manual Entry). Bottles that loaded already-transferred and are left
  // untouched keep their STORED values — matching legacy, which stamps only on a
  // real SelectionChanged action, never on the initial programmatic selection
  // (so re-opening a scanned job keeps ReceiptMode 'Scanned' + its real recorder).
  const [sessionStamped, setSessionStamped] = useState<Set<string>>(new Set());
  // Per-bottle Remark (legacy "Enter Remark" popup). The Remark grid column is a
  // checkbox = "has remark"; clicking it opens the editor. Keyed by bottleOid.
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [remarkDlg, setRemarkDlg] = useState<Bottle | null>(null);

  const [detail, setDetail]   = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [error, setError]     = useState<string | null>(null);

  const [sel, setSel]         = useState<Set<string | number>>(new Set());
  const [broughtBy, setBroughtBy] = useState<Lookup[]>([]);
  /** Sample Status + Employee lists, for the inline SampleStatus / RecordedBy cells. */
  const [statusList, setStatusList] = useState<Lookup[]>([]);
  const [employees, setEmployees] = useState<Lookup[]>([]);
  /** INLINE CELL EDITS, buffered until Save (legacy InlineEditMode="Batch" on
   *  SampleBottleAllocation_ListView_SampleTransfer). Keyed by bottle Oid. */
  const [edits, setEdits] = useState<Record<string, BottleEdit>>({});
  const [reportItems, setReportItems] = useState<{ id: number; label: string; templateName: string; allowMultipleJobId: boolean }[]>([]);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  // Editable header form (pre-filled from the clicked row on load)
  const [receivedDate, setReceivedDate] = useState<string>(nowLocalInput());
  const [relinquishers, setRelinquishers] = useState<Set<string>>(new Set());
  const [coolerId, setCoolerId]   = useState('1');
  const [coolerTemp, setCoolerTemp] = useState('');
  const [noSamples, setNoSamples]   = useState(0);
  const [noContainers, setNoContainers] = useState(0);
  const [noCoolers, setNoCoolers]   = useState(0);
  const [sampleAppearance, setSampleAppearance] = useState('');
  const [transportCondition, setTransportCondition] = useState('');
  const [comment, setComment]     = useState('');
  const [sampleStatusOid, setSampleStatusOid] = useState<string>('');

  // The transfer GROUP currently open. Starts as the row the user clicked, but a
  // Save moves the ticked bottles into a NEW group (Pending Submission), so the
  // page re-points at that group — otherwise it would sit on the group those
  // bottles just LEFT and look like the save was lost: status still "Pending
  // Transfer", the saved sample gone from the grid, Selected Samples empty
  // (user report 2026-09-23).
  // Which pill page (Samples / Tests / Containers) has REPLACED this detail view,
  // Sample Registration style (user request 2026-09-23). null = show the detail.
  const [pillPage, setPillPage] = useState<PillTab | null>(null);

  // Scan Bottle input + the bottles that were ticked BY A SCAN (they save with
  // ReceiptMode 'Scanned'; hand-ticked ones stay 'Manual Entry').
  const [scanText, setScanText] = useState('');
  const [scanned, setScanned] = useState<Set<string>>(new Set());
  const scanRef = useRef<HTMLInputElement>(null);

  const [activeKey, setActiveKey] = useState(rowKey);
  useEffect(() => { setActiveKey(rowKey); }, [rowKey]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const res = await api.get(`/api/v1/sample-transfer/${activeKey}/detail`);
      const d = (res.data as { data: Detail }).data;
      setDetail(d);
      // Fresh rows supersede any buffered inline edits - after a Save the values
      // are now IN the rows, and after a plain reload the buffer must not survive.
      setEdits({});
      // Legacy parity (SampleTransferViewController): the bottles ALREADY transferred
      // (SampleTransferStatus >= PendingSubmission, i.e. StageInt >= 1) ARE the
      // "selected" set — on opening a Saved/Submitted job they show CHECKED in the
      // All grid and populate the top "Selected Samples" grid. A not-yet-saved
      // (Pending Transfer) job has none, so it opens with an empty Selected grid.
      setSel(new Set(d.bottles.filter(b => b.stageInt >= 1).map(b => b.bottleOid)));
      // A fresh load has stamped nothing yet — already-transferred rows show STORED
      // ReceiptMode / RecordedBy until the user actually (re)ticks them.
      setSessionStamped(new Set());
      setStamps({});
      // Seed the per-bottle Remark map from the loaded rows (drives the checkbox).
      setRemarks(Object.fromEntries(d.bottles.filter(b => b.remark).map(b => [b.bottleOid, b.remark])));
      // pre-fill the editable header from the clicked row (legacy parity)
      const h = d.header;
      setReceivedDate(toLocalInput(h.receivedDate));
      setRelinquishers(new Set((h.relinquishedByIds || '').split(';').map(s => s.trim().toLowerCase()).filter(Boolean)));
      setCoolerId(h.coolerId || '1');
      setCoolerTemp(h.coolerTemp || '');
      setNoSamples(h.noSamples || 0);
      setNoContainers(h.noContainers || 0);
      setNoCoolers(h.noCoolers || 0);
      setSampleAppearance(h.sampleAppearance || '');
      setTransportCondition(h.transportCondition || '');
      setComment(h.comment || '');
      setSampleStatusOid(h.sampleStatusOid || '');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [activeKey]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    void (async () => {
      try {
        const [bb, rp, ss, rl] = await Promise.all([
          api.get('/api/v1/sample-transfer/brought-by'),
          api.get('/api/v1/sample-transfer/reports'),
          api.get('/api/v1/sample-transfer/sample-statuses'),
          api.get('/api/v1/sample-transfer/received-date-lookups'),
        ]);
        setBroughtBy((bb.data as { data: Lookup[] }).data ?? []);
        setReportItems((rp.data as { data?: typeof reportItems }).data ?? []);
        setStatusList((ss.data as { data: Lookup[] }).data ?? []);
        setEmployees((rl.data as { data?: { employees?: Lookup[] } }).data?.employees ?? []);
      } catch { /* lookups optional */ }
    })();
  }, []);

  const bottles = detail?.bottles ?? [];
  const header  = detail?.header;
  const jobOid  = header?.jobOid ?? '';

  // Legacy toolbar visibility (SampleTransferViewController.OnActivated, keyed on the
  // representative bottle's SampleTransferStatus enum → our ST-1-x jobStatusId):
  //   Pending Transfer (ST-1-1/2): Submit only; Save+Cancel (right); NO Delete.
  //   Pending Submission / Saved (ST-1-3/4): Submit + Delete; Save+Cancel (right).
  //   Submitted (ST-1-5/6): Rollback REPLACES Submit; Save/Cancel/Delete-less (grids read-only).
  const stage             = header?.jobStatusId ?? '';          // rep ST-1-x
  const isPendingTransfer = stage === 'ST-1-1' || stage === 'ST-1-2';
  const isSubmitted       = stage === 'ST-1-5' || stage === 'ST-1-6';
  const showSubmit        = !isSubmitted;                        // Submit until all submitted
  const showRollback      = isSubmitted;                         // Rollback once submitted
  const showDelete        = !isPendingTransfer;                  // Delete only after Save
  const showSaveCancel    = !isSubmitted;                        // Save/Cancel disabled once submitted

  // Project Notes — job-scoped route + live badge count (shared component).
  const notesBaseUrl = jobOid ? `/api/v1/sample-transfer/${jobOid}/project-notes` : '';
  const notesCount = useNotesCount('', null, notesBaseUrl || undefined);

  // Reports ▾ → render the assigned template (RTCR) for THIS job.
  const runReport = useCallback(async (it: { templateName: string; label: string }) => {
    setReportsOpen(false);
    if (!jobId) return;
    try {
      const res = await runLoading(
        api.get('/api/v1/sample-transfer/report-pdf',
          { params: { templateName: it.templateName, jobIds: jobId }, responseType: 'blob' }),
        { blocking: true, label: `Generating ${it.label}…` });
      const blob = res.data as Blob;
      if (res.status === 204 || !blob || blob.size === 0) { notify.warning(`No data to render for ${it.label}.`); return; }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { notify.error(`Failed to generate ${it.label}.`); }
  }, [jobId, notify]);

  // Legacy grid columns (one row per sample-bottle, tests combined). Column
  // widths are kept COMPACT so the whole set fits without horizontal scroll at a
  // normal desktop width — but each column's header always shows in FULL (the grid
  // uses auto table-layout with `white-space:nowrap` headers, so a column never
  // shrinks below its heading, matching legacy ALPACA). When the viewport is
  // genuinely narrow the grid scrolls horizontally (legacy parity) and the
  // Maximize icon expands it to the full content area.
  const cols: AppColumn<Bottle>[] = [
    { field: 'sampleId',   header: 'SampleID',   width: 110 },
    { field: 'sampleName', header: 'SampleName', width: 120 },
    // Test is the widest content column. It used to WRAP so the 12-column set fit
    // without horizontal scroll, but a wrapped value ran to three lines and made
    // the row ~3x the standard 26px height (user report 2026-09-24). Every row is
    // ONE line now: the column is wide enough for the usual test name, and a
    // longer one ellipsis-clips with the full text in the cell tooltip.
    { field: 'testName',   header: 'Test',       width: 260 },
    { field: 'bottleId',   header: 'BottleID',   width: 85 },
    // EDITABLE (legacy InlineEditMode="Batch"): the columns ALPACA leaves without
    // AllowEdit="False" AND that the BO can actually store. Edits buffer in
    // `edits` and are written by Save. Test and Remark are deliberately NOT here:
    // both are [NonPersistent] getter-only on SampleBottleAllocation.
    { field: 'phCheck',    header: 'pHCheck',    width: 90, edit: { type: 'text' } },
    { field: 'sampleStatus', header: 'SampleStatus', width: 130,
      // 'N/A' heads the list and means NO status (owner request 2026-09-28) - the
      // app already shows N/A for an unset lookup elsewhere. It is the clear path,
      // so the ✕ stays off, matching legacy grid lookups which have no clear button.
      edit: { type: 'combo', options: () => [STATUS_NONE, ...statusList.map(s => s.name)] } },
    // Remark = a checkbox ("has remark"); clicking opens the "Enter Remark" popup
    // (legacy IsRemark column + SampleBottleAllocation_DetailView_SampleTransfer_Remark).
    { field: 'remark', header: 'Remark', width: 90, align: 'center', sortable: false, filterable: false,
      text: r => ((remarks[r.bottleOid] ?? r.remark) ?? '').trim() ? 'Yes' : '',
      render: r => {
        const val = (remarks[r.bottleOid] ?? r.remark) ?? '';
        // Once Submitted (or no write perm) the grid is read-only, matching ALPACA
        // (AllowEdit=false) — the Remark cell is a display-only indicator, so clicking
        // it does NOT open the Enter Remark popup (user report 2026-09-30).
        const roGrid = isSubmitted || !canWrite;
        return (
          <input
            type="checkbox"
            className="stx-remark-cb"
            checked={val.trim().length > 0}
            readOnly
            title={val.trim() ? val : (roGrid ? '' : 'Add remark')}
            style={roGrid ? { cursor: 'default' } : undefined}
            onClick={roGrid ? undefined : (e => { e.stopPropagation(); setRemarkDlg(r); })}
          />
        );
      } },
    { field: 'relinquishedBy', header: 'RelinquishedBy', width: 130,
      // Offer only the relinquishers CHOSEN IN THE HEADER (owner report 2026-09-28:
      // the cell listed all ~191). That follows the legacy rule: ALPACA auto-fills
      // this cell only when the header names exactly ONE relinquisher, and with
      // several it deliberately leaves the cell blank so the operator says which of
      // THOSE people handed over each bottle (SampleTransferViewController.cs:667-683
      // - val.Length == 1 sets cpid/cpval, else both are nulled). Picking from the
      // full master would let a name that never relinquished this job onto a row.
      // Falls back to the full list when the header is still empty, so the cell is
      // never an empty dropdown.
      edit: { type: 'combo', clearable: true, options: () => {
        const picked = broughtBy.filter(b => relinquishers.has(b.id.toLowerCase()));
        return (picked.length ? picked : broughtBy).map(b => b.name);
      } } },
    { field: 'recordedBy', header: 'RecordedBy', width: 115,
      edit: { type: 'combo', options: () => employees.map(e => e.name), clearable: true } },
    { field: 'recordedDateTime', header: 'RecordedDateTime', width: 155,
      edit: { type: 'datetime' },
      text: r => r.recordedDateTime ? fmtLocalDateTime(r.recordedDateTime) : '',
      render: r => r.recordedDateTime ? fmtLocalDateTime(r.recordedDateTime) : '—' },
    // ReceiptMode is AllowEdit="False" in ALPACA - the app sets it ('Scanned' vs
    // 'Manual Entry'), the user never types it.
    { field: 'receiptMode', header: 'ReceiptMode', width: 115 },
  ];
  // Legacy TOP "Selected Samples" grid = same columns MINUS Remark
  // (SampleBottleAllocation_ListView_SampleTransfer_Selected). Read-only preview.
  // The SELECTED grid is read-only: ALPACA's _Selected view sets AllowEdit="False"
  // on every column, so the editors are stripped rather than inherited from `cols`.
  const selCols: AppColumn<Bottle>[] = cols
    .filter(c => c.field !== 'remark')
    .map(({ edit: _edit, ...c }) => c);

  /** Brought By as LookupCombo data - one column, the NAME (column 0 is the value). */
  const relLookup = useMemo(
    () => ({ cols: ['Relinquished By'], rows: broughtBy.map(b => [b.name]) }),
    [broughtBy]);
  /** The ticked names, derived from the id set the save still uses. */
  const relSelectedList = useMemo(
    () => broughtBy.filter(b => relinquishers.has(b.id.toLowerCase())).map(b => b.name),
    [broughtBy, relinquishers]);
  const relSelectedNames = useMemo(
    () => broughtBy.filter(b => relinquishers.has(b.id.toLowerCase())).map(b => b.name).join(', '),
    [broughtBy, relinquishers]);

  // Ticking a sample STAMPS it (legacy SelectionChanged, ReceiptMode 'Manual Entry'):
  // RecordedBy = current user, RecordedDateTime = the moment ticked. Unticking drops it.
  // Only bottles the user CHANGES here become session-stamped — a row that merely
  // loaded already-selected (already transferred) stays untouched and keeps its
  // stored values until the user actually re-ticks it (legacy stamps only on a real
  // SelectionChanged, guarded by cpselectionprocess).
  const onSelectionChange = useCallback((next: Set<string | number>) => {
    const nextStr = new Set([...next].map(String));
    const added = [...nextStr].filter(id => !sel.has(id));
    const removed = [...sel].map(String).filter(id => !nextStr.has(id));
    if (added.length || removed.length) {
      const now = nowStamp();
      setSessionStamped(prev => {
        const s = new Set(prev);
        added.forEach(id => s.add(id));
        removed.forEach(id => s.delete(id));
        return s;
      });
      setStamps(prev => {
        const out = { ...prev };
        added.forEach(id => { out[id] = now; });
        removed.forEach(id => { delete out[id]; });
        return out;
      });
    }
    setSel(next);
    // Legacy (ProcessAction "Selection"): ticking samples auto-counts the header's
    // No. Samples (distinct samples) + No. Containers (bottle rows) live.
    const picked = bottles.filter(b => next.has(b.bottleOid));
    setNoSamples(new Set(picked.map(b => b.sampleId)).size);
    setNoContainers(picked.length);
  }, [bottles, sel]);

  // Rows shown in BOTH grids: a ticked bottle carries the live stamp (RecordedBy = current
  // user, RecordedDateTime = tick time, ReceiptMode = 'Manual Entry', RelinquishedBy = the
  // header's relinquisher when exactly one is chosen); unticked keeps its stored values.
  const displayBottles = useMemo(() => {
    const relOne = relinquishers.size === 1 ? relSelectedNames : '';
    const nameOf = (list: Lookup[], id?: string | null) =>
      (id ? (list.find(x => x.id.toLowerCase() === id.toLowerCase())?.name ?? '') : '');
    return bottles.map(b0 => {
      // Buffered inline edits win over the stored value, so an edited cell keeps
      // showing what the user typed until Save (or Cancel) resolves it.
      const e = edits[b0.bottleOid];
      const b = e ? {
        ...b0,
        phCheck:          e.phCheck          ?? b0.phCheck,
        relinquishedBy:   e.relinquishedByOid !== undefined
                            ? nameOf(broughtBy, e.relinquishedByOid) : b0.relinquishedBy,
        sampleStatus:     e.sampleStatusOid  !== undefined
                            ? nameOf(statusList, e.sampleStatusOid) : b0.sampleStatus,
        sampleStatusOid:  e.sampleStatusOid  !== undefined ? e.sampleStatusOid : b0.sampleStatusOid,
        recordedBy:       e.recordedByOid    !== undefined
                            ? nameOf(employees, e.recordedByOid) : b0.recordedBy,
        recordedDateTime: e.recordedDateTime !== undefined ? e.recordedDateTime : b0.recordedDateTime,
      } : b0;
      // An unset SampleStatus stays BLANK in the cell - ALPACA shows nothing there.
      // N/A exists only as the dropdown entry that CLEARS the column (owner
      // 2026-09-28), it is not a display value.
      // Only a bottle the user (re)selected THIS session carries the live who/when/how
      // stamp. A row that merely loaded already-transferred keeps its STORED
      // ReceiptMode / RecordedBy / RecordedDateTime (legacy shows the stored value on
      // re-open — e.g. a scanned job stays 'Scanned' with its real recorder, not
      // 'Manual Entry' + the current user).
      return (
      sessionStamped.has(b.bottleOid)
        ? {
            ...b,
            // A freshly-ticked bottle is auto-stamped (who/when/how), but an EXPLICIT
            // cell edit always wins - otherwise a hand-set RecordedBy /
            // RecordedDateTime / RelinquishedBy would silently snap back to the stamp
            // on the very rows being transferred. Matches the SP, which applies the
            // edits AFTER the uniform stamp for the same reason.
            recordedBy: e?.recordedByOid !== undefined
              ? b.recordedBy : (currentUser || b.recordedBy),
            recordedDateTime: e?.recordedDateTime !== undefined
              ? b.recordedDateTime : (stamps[b.bottleOid] ?? b.recordedDateTime),
            // Legacy: a bottle ticked BY A SCAN records 'Scanned'; ticked by hand
            // records 'Manual Entry' (SampleTransferViewController L802/L838).
            receiptMode: scanned.has(b.bottleOid) ? 'Scanned' : 'Manual Entry',
            relinquishedBy: e?.relinquishedByOid !== undefined
              ? b.relinquishedBy : (relOne || b.relinquishedBy),
          }
        : b
    );});
  }, [bottles, sessionStamped, stamps, scanned, currentUser, relinquishers, relSelectedNames,
      edits, broughtBy, statusList, employees]);

  /** Commit one inline cell (legacy Batch edit). SampleStatus CASCADES to every
   *  bottle of the same sample - ALPACA does exactly this, re-assigning the
   *  chosen status across [SampleRegistration.Oid] = the edited row's sample in
   *  BOTH grids (SampleTransferViewController.cs:1588-1594) - which is why a
   *  status set on one bottle appears on all of that sample's rows. */
  // Standing rule 15: inline grid edits are invisible to installUnsavedTracking()
  // (it watches native form inputs), so the dirty state is declared explicitly -
  // leaving the form with pending cell edits now prompts before discarding them.
  useUnsavedGuard(Object.keys(edits).length > 0);

  const commitCell = useCallback((row: Bottle, field: string, value: string) => {
    const idOf = (list: Lookup[], name: string) =>
      (name ? (list.find(x => x.name === name)?.id ?? null) : null);
    setEdits(prev => {
      const next = { ...prev };
      const put = (oid: string, patch: BottleEdit) =>
        { next[oid] = { ...next[oid], ...patch }; };
      switch (field) {
        case 'phCheck':        put(row.bottleOid, { phCheck: value }); break;
        case 'relinquishedBy': put(row.bottleOid, { relinquishedByOid: idOf(broughtBy, value) }); break;
        case 'recordedBy':     put(row.bottleOid, { recordedByOid: idOf(employees, value) }); break;
        case 'recordedDateTime': put(row.bottleOid, { recordedDateTime: value || null }); break;
        case 'sampleStatus': {
          // N/A is the 'no status' sentinel, not a row in dbo.SampleStatus.
          const oid = value === STATUS_NONE ? null : idOf(statusList, value);
          bottles.filter(b => b.sampleId === row.sampleId)
                 .forEach(b => put(b.bottleOid, { sampleStatusOid: oid }));
          break;
        }
        default: break;
      }
      return next;
    });
  }, [bottles, broughtBy, employees, statusList]);

  // ── Scan Bottle (legacy NPScanBottle) ──────────────────────────────────────
  // Port of SampleTransferViewController: the textbox raises a lookup on every
  // committed value (L1022-1039), the server validates the label and SELECTS the
  // matching row (L1655-1694), and that scan-driven selection stamps the row with
  // ReceiptMode 'Scanned' rather than 'Manual Entry' (L802).
  // Label format is `JobID-SampleNo-BottleID`, e.g. 260922007-001-A — the SAME
  // barcode the Barcode Sample Custody module reads.
  const SCAN_RE = /^\d{9}-\d{3}-[A-Za-z]+$/;

  const handleScan = useCallback(async () => {
    const raw = scanText.trim();
    if (!raw) return;
    if (!SCAN_RE.test(raw)) {
      // Legacy does NOT clear the box on a failed scan — only on a hit
      // (`sample.NPScanBottle = null`, SampleTransferViewController L1675). The
      // bad label stays visible so the operator can see and correct it.
      await showDialog({ type: 'error', title: 'Scan Bottle',
        message: 'Entered an invalid format of sample label.' });
      return;
    }
    // Legacy split: everything but the last 2 chars is the SampleID, the last
    // char is the BottleID (so `260922007-001-A` → `260922007-001` + `A`).
    const sampleId = raw.slice(0, -2);
    const bottleId = raw.slice(-1).toLowerCase();
    // Legacy searches the CURRENTLY DISPLAYED grid only, so a bottle outside this
    // transfer group is correctly reported as not existing.
    const hit = bottles.find(b =>
      b.sampleId.toLowerCase() === sampleId.toLowerCase() &&
      (b.bottleId ?? '').toLowerCase() === bottleId);
    if (!hit) {
      // Box deliberately left untouched here too — legacy keeps the text.
      await showDialog({ type: 'error', title: 'Scan Bottle',
        message: 'Entered sample label does not exist.' });
      return;
    }
    // Hit: select the row and CLEAR the box — legacy SelectRowByKey(...) +
    // NPScanBottle = null (L1674-1675). The stamp (RecordedBy / RecordedDateTime /
    // ReceiptMode / RelinquishedBy) follows from the selection, as in legacy.
    // A scan is an ACTIVE selection this session, so mark it session-stamped too —
    // this is what drives the 'Scanned' display + the SP stamp. (The scan path adds
    // to `sel` directly, bypassing onSelectionChange, so it must do this itself.)
    setSel(prev => new Set(prev).add(hit.bottleOid));
    setScanned(prev => new Set(prev).add(hit.bottleOid));
    setSessionStamped(prev => new Set(prev).add(hit.bottleOid));
    setStamps(prev => ({ ...prev, [hit.bottleOid]: new Date().toISOString() }));
    setScanText('');
    // KEEP FOCUS ON THE SCAN BOX so a handheld scanner can fire the next label
    // without the operator re-clicking (client report 2026-10-01: "scanned the first
    // one then stopped"). This scan just changed `sel`, so BOTH transfer grids
    // re-render with new `rows` (the top grid even goes 0 -> 1 row) and their
    // auto-focus-first-row can pull focus off this input. ALPACA's ASPxTextBox
    // retains focus across its ValueChanged scan callback; we re-assert it AFTER the
    // re-render (rAF) so continuous scanning works regardless of the grids' focus
    // timing. (This is post-scan focus — NOT the page-open auto-focus the note below
    // deliberately omits for LIMS parity.)
    requestAnimationFrame(() => scanRef.current?.focus());
  }, [scanText, bottles]);

  // NOTE — deliberately NO auto-focus and NO input debounce here. ALPACA's
  // DetailView sets DefaultFocusedItem="JobID", so the operator clicks into Scan
  // Bottle before scanning, and the ASPxTextBox commits on ValueChanged only
  // (Enter or focus-loss) — never per keystroke. Both were added on 2026-09-23
  // and REVERTED the same day to keep G6 identical to LIMS.

  // Unticking a row drops its scanned mark, so re-ticking by hand is 'Manual Entry'.
  useEffect(() => {
    setScanned(prev => {
      const next = new Set([...prev].filter(oid => sel.has(oid)));
      return next.size === prev.size ? prev : next;
    });
  }, [sel]);

  function savePayload() {
    return {
      jobOid,
      bottleOids: Array.from(sel).map(String),
      // Bottles ticked BY A SCAN — the SP stamps these ReceiptMode 'Scanned'.
      scannedOids: Array.from(scanned).filter(o => sel.has(o)),
      // Bottles the user actually (re)selected this session — ONLY these are
      // (re)stamped who/when/how by the SP. Untouched already-transferred rows keep
      // their stored RecordedBy / ScanDateTime / ReceiptMode (legacy parity: a
      // no-op re-save must not turn a scanned job into 'Manual Entry').
      stampedOids: Array.from(sessionStamped).filter(o => sel.has(o)),
      receivedDate: receivedDate ? receivedDate + ':00' : null,
      relinquishedBy: Array.from(relinquishers).join('; '),
      coolerId: coolerId || null,
      coolerTemp: coolerTemp || null,
      remark: null,
      comment: comment || null,
      sampleStatusOid: sampleStatusOid || null,
      noOfSamples: noSamples || sel.size,
      noContainers, noCoolers,
      sampleAppearance: sampleAppearance || null,
      transportCondition: transportCondition || null,
      // Per-bottle inline edits, buffered since the last load. Only the cells
      // actually touched travel, so the SP never clears a column nobody edited.
      // `touched` names the cells the user actually edited. JSON cannot tell
      // "absent" from "cleared to null" for the Oid columns, and clearing a
      // lookup is legitimate (ALPACA nulls the property when the cell is empty),
      // so the SP keys off this list rather than off null-ness.
      bottleEdits: Object.entries(edits).map(([bottleOid, e]) =>
        ({ bottleOid, ...e, touched: Object.keys(e).join(',') })),
    };
  }
  function preValidate(): string | null {
    if (sel.size === 0) return 'Select the samples.';
    if (!receivedDate) return 'Select the received date.';
    if (relinquishers.size === 0) return 'Select the relinquished by.';
    // EVERY selected bottle must carry a relinquisher, not just the header
    // (owner report 2026-09-28: ALPACA refuses the save, we let it through).
    // Legacy runs the same guard on Save AND Submit:
    //   SelectedObjects.Where(a => a.BroughtBy == null).Count() > 0
    //     -> "Select the relinquished by."   (SampleTransferViewController
    //        .cs:1895 Save, :2044 Submit)
    // It bites when the header names SEVERAL relinquishers: the cell is then
    // deliberately NOT auto-filled (the val.Length == 1 rule), so each ticked
    // row has to be set by hand and an untouched one would otherwise save blank.
    if (displayBottles.some(b => sel.has(b.bottleOid) && !b.relinquishedBy))
      return 'Select the relinquished by.';
    return null;
  }

  const doSaveOrSubmit = useCallback(async (submit: boolean) => {
    const bad = preValidate();
    if (bad) { notify.error(bad); return; }
    const ok = await confirmDlg({
      title: submit ? 'Submit' : 'Save',
      message: submit ? 'Do you want to submit the selected samples?' : 'Do you want to Save?',
    });
    if (!ok) return;
    try {
      const res = await runLoading(
        api.post(`/api/v1/sample-transfer/${submit ? 'submit' : 'save'}`, savePayload()),
        { blocking: true, label: submit ? 'Submitting…' : 'Saving…' });
      const n = (res.data?.data?.affected as number) ?? 0;
      await showDialog({ type: 'success', title: submit ? 'Submitted' : 'Saved',
        message: submit ? `${n} bottle${n === 1 ? '' : 's'} submitted successfully.` : 'Saved successfully.' });
      // Submit returns to the list. Save stays on the page but re-points at the
      // group it just created (savedRowKey = MAX(Oid) of the affected bottles),
      // so the header reads "Pending Submission", the saved bottles come back
      // CHECKED and the "Selected Samples" grid is populated — the same state the
      // user gets by going back to the list and opening the saved row. Without
      // this the page reloaded the OLD key, which is still Pending Transfer and
      // (since 2026-09-23 the grid is stage-scoped like legacy) no longer even
      // contains the saved bottles. Changing activeKey re-runs load() via its dep.
      if (submit) { onBack(); return; }
      const savedKey = (res.data as { data?: { savedRowKey?: string | null } })?.data?.savedRowKey;
      if (savedKey && savedKey !== activeKey) setActiveKey(savedKey); else await load();
    } catch (e: unknown) {
      notify.error(errMsg(e, 'Failed to save the transfer.'));
    }
    // `edits` and `displayBottles` MUST stay in this list. Both preValidate and
    // savePayload are plain functions closed over the render that created this
    // callback, so leaving them out pinned an OLD `edits` ({} on first render):
    // the per-bottle relinquisher guard then saw every cell as empty and refused
    // the save with "Select the relinquished by." even though the cell plainly
    // showed a name - and had it passed, savePayload would have posted an EMPTY
    // bottleEdits and dropped the edits silently. (owner report 2026-09-28; the
    // exhaustive-deps suppression below is why the compiler never flagged it.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, receivedDate, relinquishers, coolerId, coolerTemp, comment, noSamples, noContainers, noCoolers, sampleAppearance, transportCondition, sampleStatusOid, jobOid, confirmDlg, notify, load, onBack, activeKey, edits, displayBottles]);

  const doScope = useCallback(async (isDelete: boolean) => {
    const reason = await gateReason('Sample Transfer', isDelete ? 'delete' : 'edit');
    if (reason === null) return;
    const ids = Array.from(sel).map(String);
    const all = ids.length === 0;
    const ok = await confirmDlg({ title: isDelete ? 'Delete' : 'Rollback', tone: 'danger',
      message: isDelete ? 'Are you sure, Do you want to delete?' : 'Are you sure, Do you want to rollback?' });
    if (!ok) return;
    try {
      const res = await runLoading(
        api.post(`/api/v1/sample-transfer/${isDelete ? 'delete' : 'rollback'}`,
          { jobOid, bottleOids: ids, allBottles: all, reason: reason || null }),
        { blocking: true, label: isDelete ? 'Deleting…' : 'Rolling back…' });
      const n = (res.data?.data?.affected as number) ?? 0;
      await showDialog({ type: 'success', title: isDelete ? 'Deleted' : 'Rollback',
        message: isDelete ? `${n} bottle${n === 1 ? '' : 's'} reset to Pending Transfer.` : `${n} bottle${n === 1 ? '' : 's'} rolled back.` });
      onBack();
    } catch (e: unknown) {
      notify.error(errMsg(e, isDelete ? 'Failed to delete the transfer.' : 'Failed to rollback.'));
    }
  }, [sel, gateReason, confirmDlg, notify, onBack, jobOid]);

  // Sample Transfer detail action shortcuts (CLAUDE.md standing rule 14, §18.6).
  // Delete (onDelete) is auto-registered by <AppToolbar>; the left-slot buttons
  // are registered here next to where they render.
  useHotkeys([
    { combo: 'alt+o', label: 'Reports', group: 'Sample Transfer', order: 1, disabled: reportItems.length === 0, run: () => setReportsOpen(o => !o) },
    { combo: 'alt+j', label: 'Project Notes', group: 'Sample Transfer', order: 2, disabled: !jobOid, run: () => setNotesOpen(true) },
    { combo: 'alt+u', label: 'Submit', group: 'Sample Transfer', order: 3, disabled: !showSubmit || !canWrite, run: () => { void doSaveOrSubmit(true); } },
    { combo: 'mod+s', label: 'Save', group: 'Sample Transfer', order: 4, disabled: !showSaveCancel || !canWrite, run: () => { void doSaveOrSubmit(false); } },
    { combo: 'alt+b', label: 'Rollback', group: 'Sample Transfer', order: 5, disabled: !showRollback || !canWrite, run: () => { void doScope(false); } },
    { combo: 'esc', label: 'Back', group: 'Sample Transfer', order: 9, allowInInput: false, run: onBack },
  ]);

  // ── Render ──
  // A pill REPLACES the detail with its full page (Sample Registration parity),
  // rather than opening a modal over it — a 1180px dialog cannot show the ~48
  // column Sample Login grid, and it hid the job context behind itself.
  if (pillPage && header) {
    return (
      <SampleTransferSamplesPage
        jobOid={jobOid} jobId={jobId} initialTab={pillPage} submitted={isSubmitted}
        onBack={() => setPillPage(null)} />
    );
  }

  return (
    <div className="bl-page stx-detail stx-scrollpage">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span className="dp-bc-link" onClick={onBack}>Sample Transfer</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{jobId}</span>
          </div>
          {/* Detail view mirrors legacy: NO load-time / Audit chips in the toolbar
              (the Sample Transfer LIST keeps them). FormTitle is kept with empty meta
              so the shared FormMetaContext is cleared and AppToolbar shows no chips. */}
          <div className="dp-title-row">
            <FormTitle pageId="samptransfer" title={`Sample Transfer – ${jobId}`} />
            {/* Save + Cancel are NOT here any more — they moved onto the toolbar
                row (user request 2026-09-23), see AppToolbar `right=` below. */}
          </div>
        </div>

        {/* Toolbar — ONE ROW at the top (legacy parity: actions above the header) */}
        <div className="stx-toolbar-row">
          <AppToolbar
            left={
              <>
                {reportItems.length > 0 && (
                  <span className="app-toolbar__export">
                    <button className="ca-btn" onClick={() => setReportsOpen(o => !o)} title={`Reports (${formatCombo('alt+o')})`}>
                      <span className="tb-i tb-i-blue"><i className="ti ti-file-text" /></span> Reports ▾
                    </button>
                    {reportsOpen && (
                      <>
                        <div className="app-toolbar__export-backdrop" onClick={() => setReportsOpen(false)} />
                        <div className="app-toolbar__export-menu" role="menu">
                          {reportItems.map(it => (
                            <button key={it.id} className="app-toolbar__export-item" role="menuitem" onClick={() => runReport(it)}>
                              <i className="ti ti-file-text" /> {it.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </span>
                )}
                <button className="ca-btn" onClick={() => setNotesOpen(true)} disabled={!jobOid} title={`Project Notes (${formatCombo('alt+j')})`}>
                  <span className="tb-i"><i className="ti ti-notes" /></span> Project Notes{notesCount ? ` (${notesCount})` : ''}
                </button>
                {showSubmit && canWrite && (
                  <button className="ca-btn" onClick={() => doSaveOrSubmit(true)} title={`Submit (${formatCombo('alt+u')})`}><span className="tb-i">✓</span> Submit</button>
                )}
                {showRollback && canWrite && (
                  <button className="ca-btn" onClick={() => doScope(false)} title={`Rollback (${formatCombo('alt+b')})`}><span className="tb-i">↺</span> Rollback</button>
                )}
              </>
            }
            onDelete={showDelete && canDelete ? () => doScope(true) : undefined}
            /* Save + Cancel sit on the SAME ROW as Reports / Project Notes /
               Submit / Delete, pushed to the RIGHT end (user request
               2026-09-23 — they were previously a separate row above the
               toolbar). The ONE primary Save for the form, shown until the job
               is Submitted; Cancel discards edits and returns to the list — legacy
               XAF Cancel (ModificationsController.CancelAction) rolls back and closes
               the detail back to the ListView (user report 2026-09-30). */
            right={showSaveCancel && canWrite ? (
              <span className="stx-save-cancel">
                {/* Keeps the Ctrl/⌘+S hint added upstream with the hotkey layer. */}
                <button className="ca-btn ca-btn-solid" onClick={() => doSaveOrSubmit(false)} title={`Save (${formatCombo('mod+s')})`}>
                  <span className="tb-i">💾</span> Save
                </button>
                <button className="ca-btn" onClick={() => { resetUnsavedTracking(); onBack(); }} title="Cancel">
                  <span className="tb-i">✕</span> Cancel
                </button>
              </span>
            ) : undefined}
          />
        </div>

        {/* Two-column header (label : field) — legacy DetailView parity */}
        {header && (
          <div className="stx-hdr2">
            {/* LEFT column */}
            <div className="stx-col">
              <div className="stx-row"><label>Job ID:</label><div className="stx-ro">{header.jobId}</div></div>
              <div className="stx-row"><label>Received Date:</label>
                <input className="stx-edit" type="datetime-local" value={receivedDate} onChange={e => setReceivedDate(e.target.value)} /></div>
              <div className="stx-row"><label>Received By:</label><div className="stx-ro">{header.receivedBy || '—'}</div></div>
              <div className="stx-row req"><label>Relinquished By:<span className="stx-req">*</span></label>
                {/* The SHARED multi-select combo, same as Sample Registration uses for
                    Sample Matrix (owner request 2026-09-24): orange caret button, the
                    filter box and checkbox rows. This REPLACES a hand-rolled .stx-combo
                    popup (standing rule 8 - never reinvent a shared control).
                    No (Select All): that row belongs to the Sample Registration COPY of
                    LookupCombo; the shared one only offers it behind multiActions, which
                    is left off.
                    Selection stays keyed on the Brought By Oid - the save posts
                    relinquishedBy as joined ids - so the combo works in NAMES and is
                    mapped back here. Safe because BroughtByName is required + unique. */}
                <LookupCombo
                  multi
                  lookup={relLookup}
                  values={relSelectedList}
                  placeholder="Select…"
                  popupMatchTrigger
                  onToggle={next => {
                    const wanted = new Set(next);
                    setRelinquishers(new Set(
                      broughtBy.filter(b => wanted.has(b.name)).map(b => b.id.toLowerCase())));
                  }}
                /></div>
              <div className="stx-row"><label>Client:</label><div className="stx-ro">{header.client}</div></div>
              <div className="stx-row"><label>Project ID:</label><div className="stx-ro">{header.projectId}</div></div>
              <div className="stx-row"><label>Project Name:</label><div className="stx-ro">{header.projectName}</div></div>
              <div className="stx-row"><label>Project Location:</label><div className="stx-ro">{header.projectLocation || '—'}</div></div>
              <div className="stx-row"><label>Sample Matrix:</label><div className="stx-ro">{header.sampleMatrix}</div></div>
              <div className="stx-row stx-row-multi"><label>Test Summary:</label>
                <div className="stx-ro stx-ro-multi">{header.testSummary}</div></div>
            </div>
            {/* RIGHT column */}
            <div className="stx-col">
              <div className="stx-row"><label>No. Samples:</label>
                <input type="number" value={noSamples} onChange={e => setNoSamples(+e.target.value || 0)} /></div>
              <div className="stx-row"><label>No. Containers:</label>
                <input type="number" value={noContainers} onChange={e => setNoContainers(+e.target.value || 0)} /></div>
              <div className="stx-row"><label>No. Coolers:</label>
                <input type="number" value={noCoolers} onChange={e => setNoCoolers(+e.target.value || 0)} /></div>
              <div className="stx-row"><label>Cooler Id:</label>
                <input value={coolerId} onChange={e => setCoolerId(e.target.value)} /></div>
              <div className="stx-row"><label>Cooler Temp:</label>
                <input className="stx-edit" value={coolerTemp} onChange={e => setCoolerTemp(e.target.value)} /></div>
              <div className="stx-row"><label>Sample Appearance:</label>
                <input className="stx-edit" value={sampleAppearance} onChange={e => setSampleAppearance(e.target.value)} /></div>
              <div className="stx-row"><label>Transport Conditions:</label>
                <input className="stx-edit" value={transportCondition} onChange={e => setTransportCondition(e.target.value)} /></div>
              <div className="stx-row"><label>Comment:</label>
                <input value={comment} onChange={e => setComment(e.target.value)} /></div>
              <div className="stx-row"><label>Status:</label>
                <div className="stx-ro"><span className={`bl-pill ${pill(header.jobStatusId)}`}>{transferStatusName(header.jobStatusId) || header.jobStatusId}</span></div></div>
              {/* Scan Bottle — legacy NPScanBottle. HIDDEN once the row is
                  Submitted (SampleTransferViewController L533-543). A handheld
                  scanner types the label and sends Enter. */}
              {!isSubmitted && (
                <div className="stx-row"><label>Scan Bottle:</label>
                  <input className="stx-edit" ref={scanRef} value={scanText}
                    onChange={e => setScanText(e.target.value)}
                    // Legacy ASPxTextBox ValueChanged semantics exactly: the value
                    // is committed on Enter or on losing focus, never per keystroke.
                    // Tab is NOT intercepted — it moves focus, and the resulting
                    // blur commits, which is what LIMS does.
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void handleScan(); } }}
                    onBlur={() => { if (scanText.trim()) void handleScan(); }}
                    autoComplete="off" /></div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bl-grid-area">

        {/* TOP grid — SELECTED / scanned samples (legacy _Selected grid). Shown only
            while the job is NOT yet Submitted (legacy hides it once Submitted); a live
            preview of the rows ticked in the All-samples grid below. */}
        {!isSubmitted && (
          <div className="bl-card stx-selgrid-card">
            <AppDataGrid<Bottle>
              rows={displayBottles.filter(b => sel.has(b.bottleOid))}
              columns={selCols}
              getRowId={r => r.bottleOid}
              maximizable
              /* Legacy page size for THIS grid: ALPACA Model.xafml sets
                 PageSize="10" on SampleBottleAllocation_ListView_SampleTransfer_Selected
                 (the DashboardViewItem SelectedSampleGrid). The app-wide default is 100. */
              defaultPageSize={10}
              exportFilename={`sample-transfer-${jobId}-selected`}
              emptyText="No data to display"
            />
          </div>
        )}

        {/* BOTTOM grid — ALL sample-bottles of the job (the pool; tick to select) */}
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err" style={{ margin: 12 }}>
              <i className="ti ti-alert-circle" /> {error}
              <button onClick={load} className="stx-retry">Retry</button>
            </div>
          ) : (
            <AppDataGrid<Bottle>
              rows={displayBottles}
              columns={cols}
              getRowId={r => r.bottleOid}
              maximizable
              /* Inline Batch editing, ALPACA parity. Only this grid - the
                 Selected one is read-only (see selCols).
                 Once SUBMITTED the grid is READ-ONLY with NO selection checkbox,
                 matching ALPACA's SampleTransferViewController (AllowEdit=false +
                 CanSelectRows=false when SampleTransferStatus == Submitted; user
                 report 2026-09-30). AppDataGrid drops inline editing when
                 onCellCommit is absent, and hides the checkbox column when
                 selectable is false. Rollback still works — doScope falls back to
                 allBottles when nothing is selected. */
              onCellCommit={isSubmitted ? undefined : (row, col, val) => commitCell(row, String(col.field), val)}
              exportFilename={`sample-transfer-${jobId}-samples`}
              selectable={!isSubmitted}
              selectedIds={sel}
              onSelectionChange={onSelectionChange}
              loading={loading}
              loadMs={loadMs}
              emptyText="No samples for this job"
            />
          )}
        </div>

        {header && (
          <div className="bl-card stx-tabs-card">
            <SampleTransferBottomTabs
              onOpenPill={setPillPage}
              jobOid={jobOid}
              jobId={jobId}
              receivedDate={header.receivedDate}
              receivedDates={detail?.receivedDates ?? []}
              samplesCount={header.samplesCount}
              testsCount={header.testsCount}
              containersCount={header.containersCount}
              canWrite={canWrite}
              canCreate={canCreate}
              canDelete={canDelete}
              broughtBy={broughtBy}
              relinquishers={relinquishers}
            />
          </div>
        )}

        {header?.dueDate && (
          <div className="stx-due">Due date: {fmtLocalDate(header.dueDate)}</div>
        )}
      </div>

      {notesOpen && jobOid && (
        <NotesListPopup
          batchId=""
          batchTest="Sample Transfer"
          batchLabel={jobId}
          businessObject="Sample Transfer"
          jobOid={null}
          baseUrl={notesBaseUrl}
          onClose={() => setNotesOpen(false)}
        />
      )}

      {remarkDlg && (
        <RemarkDialog
          jobOid={jobOid}
          bottle={remarkDlg}
          initial={remarks[remarkDlg.bottleOid] ?? remarkDlg.remark ?? ''}
          readOnly={isSubmitted || !canWrite}
          onClose={() => setRemarkDlg(null)}
          onSaved={text => {
            setRemarks(prev => ({ ...prev, [remarkDlg.bottleOid]: text }));
            setRemarkDlg(null);
          }}
        />
      )}
    </div>
  );
}

/** Legacy "Enter Remark" popup — a single Remark textarea + OK / Cancel. Read-only
 *  when the job is already Submitted (mirrors ViewEditMode.View). Saves the one
 *  bottle's Remark immediately (PUT), so the grid checkbox reflects it. */
function RemarkDialog({ jobOid, bottle, initial, readOnly, onClose, onSaved }: {
  jobOid: string; bottle: Bottle; initial: string; readOnly: boolean;
  onClose: () => void; onSaved: (text: string) => void;
}) {
  const notify = useNotify();
  const [text, setText] = useState(initial);
  // Bespoke popup keyboard: Esc closes (standing rule 14).
  const modalKeys = useModalKeys(true, { onClose });
  const save = useCallback(async () => {
    try {
      await runLoading(
        api.put(`/api/v1/sample-transfer/${jobOid}/bottles/${bottle.bottleOid}/remark`, { remark: text }),
        { blocking: true, label: 'Saving…' });
      notify.success('Saved successfully.');
      onSaved(text);
    } catch (e) { notify.error(errMsg(e, 'Failed to save the remark.')); }
  }, [jobOid, bottle.bottleOid, text, notify, onSaved]);

  return (
    <div className="stx-modal-backdrop" onClick={onClose}>
      <div className="stx-modal stx-modal-remark" onClick={e => e.stopPropagation()} {...modalKeys}>
        <StxModalHead title="Enter Remark" onClose={onClose} />
        <div className="stx-modal-body">
          <div className="stx-mrow stx-mrow-area">
            <label>Remark:</label>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              rows={8}
              readOnly={readOnly}
              autoFocus={!readOnly}
            />
          </div>
        </div>
        <div className="stx-modal-foot">
          {!readOnly && <button className="stx-tbtn stx-primary" onClick={save}>OK</button>}
          <button className="stx-tbtn" onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</button>
        </div>
      </div>
    </div>
  );
}

function errMsg(e: unknown, fallback: string): string {
  const anyE = e as { response?: { data?: { error?: { message?: string } } } };
  return anyE?.response?.data?.error?.message ?? fallback;
}
